"use client";

import { toast as sonnerToast } from "sonner";

import { CustomToast } from "@/components/CustomToast";
import { useToasterStore } from "@/store/useToasterStore.ts";

type ToasterPosition =
  | "top-left"
  | "top-center"
  | "top-right"
  | "bottom-left"
  | "bottom-center"
  | "bottom-right";

interface ToastOptions {
  title?: string;
  description?: string;
  type?: "default" | "success" | "error" | "warning" | "info" | "loading" | "important";
  icon?: string;
  duration?: number;
  audio?: boolean;
  position?: ToasterPosition;
  action?: {
    label: string;
    onClick: () => void;
  };
  cancel?: {
    label: string;
    onClick: () => void;
  };
}

// Main toast function using Sonner's custom API
function createToast(message: string, options: ToastOptions = {}) {
  const { setPosition } = useToasterStore.getState();

  if (options.position) {
    setPosition(options.position);
  } else {
    // Reset to default if no position is provided for this toast
    setPosition("top-center");
  }

  return sonnerToast.custom(
    (id) => (
      <CustomToast
        action={options.action}
        audio={options.audio}
        cancel={options.cancel}
        description={options.description}
        icon={options.icon}
        id={id}
        title={message}
        type={options.type || "default"}
        visible={true}
        onDismiss={() => sonnerToast.dismiss(id)}
      />
    ),
    {
      duration: options.duration ?? 4000,
    },
  );
}

// Define the toast function with proper typing
interface CustomToastFunction {
  (message: string, options?: ToastOptions): string | number;

  success: (message: string, options?: Omit<ToastOptions, "type">) => string | number;
  error: (message: string, options?: Omit<ToastOptions, "type">) => string | number;
  warning: (message: string, options?: Omit<ToastOptions, "type">) => string | number;
  info: (message: string, options?: Omit<ToastOptions, "type">) => string | number;
  loading: (message: string, options?: Omit<ToastOptions, "type">) => string | number;
  important: (message: string, options?: Omit<ToastOptions, "type">) => string | number;
  promise: <T>(
    promise: Promise<T>,
    options: {
      loading: string;
      success: string | ((data: T) => string);
      error: string | ((error: any) => string);
    } & Omit<ToastOptions, "type">,
  ) => string | number;
  dismiss: (id?: string | number) => void;
  dismissAll: () => void;
}

// Export the toast functions
export const toast = createToast as CustomToastFunction;

// Toast variants
toast.success = (message: string, options?: Omit<ToastOptions, "type">) => {
  return createToast(message, { ...options, type: "success" });
};

toast.error = (message: string, options?: Omit<ToastOptions, "type">) => {
  return createToast(message, { duration: 10000, ...options, type: "error" });
};

toast.warning = (message: string, options?: Omit<ToastOptions, "type">) => {
  return createToast(message, { ...options, type: "warning" });
};

toast.info = (message: string, options?: Omit<ToastOptions, "type">) => {
  return createToast(message, { ...options, type: "info" });
};

toast.loading = (message: string, options?: Omit<ToastOptions, "type">) => {
  return createToast(message, { ...options, type: "loading" });
};

toast.important = (message: string, options?: Omit<ToastOptions, "type">) => {
  return createToast(message, { ...options, type: "important", audio: true });
};

// Promise toast
toast.promise = function <T>(
  promise: Promise<T>,
  options: {
    loading: string;
    success: string | ((data: T) => string);
    error: string | ((error: any) => string);
  } & Omit<ToastOptions, "type">,
) {
  return sonnerToast.promise(promise, {
    loading: (
      <CustomToast
        description={options.description}
        icon={options.icon}
        id="loading"
        title={options.loading}
        type="loading"
        visible={true}
        onDismiss={() => {}}
      />
    ),
    success: (data) => (
      <CustomToast
        description={options.description}
        icon={options.icon}
        id="success"
        title={typeof options.success === "function" ? options.success(data) : options.success}
        type="success"
        visible={true}
        onDismiss={() => {}}
      />
    ),
    error: (error) => (
      <CustomToast
        description={options.description}
        icon={options.icon}
        id="error"
        title={typeof options.error === "function" ? options.error(error) : options.error}
        type="error"
        visible={true}
        onDismiss={() => {}}
      />
    ),
    duration: options.duration ?? 4000,
  }) as string | number;
};

// Dismiss functions
toast.dismiss = (id?: string | number) => {
  if (id) {
    sonnerToast.dismiss(id);
  } else {
    sonnerToast.dismiss();
  }
};

toast.dismissAll = () => {
  sonnerToast.dismiss();
};

export default toast;
